import type { AplConfigurationSummary } from './types'
import { getCsrfToken } from '../auth/authApi'
import { apiFetch } from '../../shared/http/apiFetch'

export async function getAplConfigurations(
  signal?: AbortSignal,
): Promise<AplConfigurationSummary[]> {
  const response = await apiFetch('/api/v1/apl/configurations', { signal })
  if (!response.ok) {
    throw new Error('aplPanel.loadError')
  }

  return response.json() as Promise<AplConfigurationSummary[]>
}

export type AplConfigurationAction = 'suspend' | 'reactivate' | 'retry'

export type CreateAplConfigurationRequest = {
  code: string
  name: string
  defaultCulture: string
  companyCode: string
  companyName: string
  administratorUserName: string
  administratorDisplayName: string
  temporaryPassword: string
  administratorLicenseQuantity: number
}

export async function executeAplConfigurationAction(
  configurationId: string,
  action: AplConfigurationAction,
): Promise<string> {
  const csrfToken = await getCsrfToken()
  const actionPath = action === 'retry'
    ? 'provisioning-retries'
    : action
  const response = await apiFetch(
    `/api/v1/apl/configurations/${configurationId}/${actionPath}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-CSRF-TOKEN': csrfToken },
    },
  )
  const payload = (await response.json().catch(() => null)) as {
    code?: string
  } | null
  if (!response.ok) {
    throw new Error(payload?.code ?? 'aplPanel.actionError')
  }

  return payload?.code ?? 'aplPanel.actionCompleted'
}

export async function createAplConfiguration(
  request: CreateAplConfigurationRequest,
): Promise<string> {
  const csrfToken = await getCsrfToken()
  const response = await apiFetch('/api/v1/apl/configurations', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify(request),
  })
  const payload = (await response.json().catch(() => null)) as {
    code?: string
  } | null
  if (!response.ok) {
    throw new Error(payload?.code ?? 'aplPanel.actionError')
  }

  return payload?.code ?? 'aplPanel.configurationCreated'
}
